/**
 * Presentation helpers for the Operator Admin screens (English copy).
 *
 * Only the English words live here. Everything else is the driver portal's
 * (portalFormat.js) and the app's timezone plumbing (Utils/timezone.js):
 * every date is read in Europe/Budapest, never the device clock, so "Today"
 * means the Budapest day wherever the operator's laptop is. Times come
 * straight from `formatTime` / `formatDate`. Day and month names are
 * dayjs's built-in English, set per instance (`.locale('en')`), so the
 * output is the same whatever global dayjs locale is loaded.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate, formatTime, utcToLocal } from '../../../Utils/timezone';
import { PUSH_SUPPORT } from '../../../Services/PushNotifications';
import { OPERATOR_ROLE_LABELS } from '../../../config/bookingConstants';
import {
  errorMessage, formatDistance, formatDuration, formatHours, groupDigits, phoneReleaseAt, place,
  positiveCount, scanReasonLines, vehicleLabel,
} from '../portalFormat';

dayjs.extend(utc);

// Keeps amounts and units on one line: '9 000 Ft', never '9' / '000 Ft'.
const NBSP = '\u00a0';

/** A Budapest-time dayjs that names days and months in English, or null. */
const inEnglish = (value) => (value ? utcToLocal(value).locale('en') : null);

/** 'Wednesday 16 September' */
export const longDay = (value) => inEnglish(value)?.format('dddd D MMMM') || '';

/** '16 Sep' */
export const shortDay = (value) => inEnglish(value)?.format('D MMM') || '';

/**
 * 'Phone from 18 Sep 08:28' (Budapest) — while the API still withholds the
 * customer's number (it sends it from 24 hours before pickup), else ''.
 */
export const phoneReleaseText = (trip) => {
  const at = phoneReleaseAt(trip);
  return at ? `Phone from ${shortDay(at)} ${formatTime(at)}` : '';
};

/** '3 trips' / '1 trip' */
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Every period `rangeFor` understands, as a tab names it. */
const RANGE_LABELS = {
  yesterday: 'Yesterday',
  today: 'Today',
  tomorrow: 'Tomorrow',
  week: 'This week',
  month: 'This month',
};

/** `[{ key, label }]` tabs for the given `rangeFor` keys, in that order. */
export const rangeOptions = (keys) => keys.map((key) => ({ key, label: RANGE_LABELS[key] }));

export const ASSIGNMENT_RANGES = rangeOptions(['today', 'week', 'month']);

/**
 * 'Wednesday 16 September' for a one-day range, '14 Sep – 20 Sep' for a
 * longer one — the heading over a list read through `rangeFor`.
 */
export const rangeHeading = ({ from, to }) => (from !== to
  ? `${shortDay(`${from}T12:00:00Z`)} – ${shortDay(`${to}T12:00:00Z`)}`
  : longDay(`${from}T12:00:00Z`));

/**
 * Today's Budapest calendar date as a plain UTC date, for day and month
 * arithmetic. Arithmetic on a timezone-bound dayjs keeps its starting UTC
 * offset, so stepping from summer time into winter time lands an hour
 * early — on the previous day. A calendar date in UTC has no DST to trip on.
 */
const budapestCalendarDay = (now) => dayjs.utc(formatDate(now)).locale('en');

const ymd = (day) => day.format('YYYY-MM-DD');

/**
 * `{ from, to }` (Budapest YYYY-MM-DD) for Yesterday / Today / Tomorrow /
 * This week (Mon–Sun) / This month, relative to `now`.
 *
 * The one place the portal's client-side day arithmetic happens — the
 * operator's three tabs and the dispatcher board's four read their window
 * through it, so "This week" can only ever mean one thing. Its server-side
 * twin is ph-core's portalRange.helper.ts.
 *
 * Each screen names the keys IT offers (`ASSIGNMENT_RANGES`,
 * `DISPATCHER_RANGES`), so a key added here for one of them changes nothing
 * on the other: `yesterday` is the dispatch board's — the office is asked
 * about a trip that has already run, and an operator is not.
 */
export const rangeFor = (key, now = new Date()) => {
  const today = budapestCalendarDay(now);
  if (key === 'yesterday') {
    const day = ymd(today.subtract(1, 'day'));
    return { from: day, to: day };
  }
  if (key === 'tomorrow') {
    const day = ymd(today.add(1, 'day'));
    return { from: day, to: day };
  }
  if (key === 'week') {
    const start = today.subtract((today.day() + 6) % 7, 'day');
    return { from: ymd(start), to: ymd(start.add(6, 'day')) };
  }
  if (key === 'month') {
    return { from: ymd(today.startOf('month')), to: ymd(today.endOf('month')) };
  }
  return { from: ymd(today), to: ymd(today) };
};

/** The calendar month `offset` months from `now`'s, as a statement range. */
export const monthRange = (offset = 0, now = new Date()) => {
  const month = budapestCalendarDay(now).startOf('month').add(offset, 'month');
  return {
    from: ymd(month),
    to: ymd(month.endOf('month')),
    label: month.format('MMMM YYYY'),
  };
};

const UNITS = { HUF: 'Ft', EUR: '€' };

/**
 * '9 000 Ft' / '40 €' / '40,50 €' — money as the operator reads it, grouped
 * the way the driver screens group it (portalFormat's groupDigits).
 * Returns '—' for a missing amount so a table cell is never blank.
 */
export const formatMoney = (amount, currency) => {
  const n = Number(amount);
  if (amount === null || amount === undefined || amount === '' || !Number.isFinite(n)) return '—';
  const decimals = currency === 'HUF' || Number.isInteger(n) ? 0 : 2;
  const sign = n < 0 ? `−${NBSP}` : '';
  const unit = UNITS[currency] || currency;
  return `${sign}${groupDigits(Math.abs(n), decimals)}${unit ? `${NBSP}${unit}` : ''}`;
};

/**
 * A statement as its CURRENCY BUCKETS — always at least one, never summed.
 *
 * A company that settles in two currencies holds two operator records, one
 * per currency, and a login may now answer for both. `/portal/operator/
 * statement` sends the login's OWN record as the scalars it has always sent,
 * and — only when there is more than one — the whole set under `by_operator`.
 *
 * This turns either shape into the same list, so a screen renders one bucket
 * the way it always did and two the same way twice. Nothing here adds a
 * figure to another figure: there is no rate at which a EUR number and a HUF
 * number make a meaningful total, and having two records is precisely what
 * stops anyone reaching for one.
 */
export const statementBuckets = (statement) => {
  if (!statement) return [];

  const grouped = statement.by_operator;

  if (Array.isArray(grouped) && grouped.length > 1) {
    return grouped.map((bucket) => ({
      ...bucket,
      lines: Array.isArray(bucket.lines) ? bucket.lines : [],
    }));
  }

  return [{
    operator_id: statement.operator_id,
    currency: statement.currency,
    trip_count: statement.trip_count,
    fees: statement.fees,
    cash_collected: statement.cash_collected,
    paid: statement.paid,
    outstanding: statement.outstanding,
    lines: Array.isArray(statement.lines) ? statement.lines : [],
  }];
};

/** An `{ amount, currency }` from the API, or '—'. */
export const formatAmountView = (view) => (view ? formatMoney(view.amount, view.currency) : '—');

/** '3 pax' */
export const paxLabel = (passengers) => {
  const p = passengers || {};
  const people = positiveCount(p.amount) || positiveCount(p.adults) + positiveCount(p.children);
  return people ? `${people} pax` : '';
};

const SEATS = [
  ['booster_seat', 'booster seat'],
  ['convertible_seat', 'child seat'],
  ['infant_carrier', 'infant carrier'],
];

/** ['1 booster seat'] — the child seats the driver must bring. */
const seatLabels = (passengers) => SEATS
  .map(([field, name]) => [positiveCount(passengers?.[field]), name])
  .filter(([seats]) => seats)
  .map(([seats, name]) => `${seats} ${name}${seats > 1 ? 's' : ''}`);

/** 'Business · 3 pax · 1 booster seat · BA864' */
export const tripMeta = (trip) => [
  trip?.category,
  paxLabel(trip?.passengers),
  ...seatLabels(trip?.passengers),
  trip?.flight_number,
].filter(Boolean).join(' · ');

/**
 * 'Hotel Gellért → BUD Terminal 2B' — the first part of each address. Takes
 * a trip (`from: { address }`) or a statement line (`from: 'address'`).
 */
export const routeLabel = (trip) => [place(trip?.from).title, place(trip?.to).title]
  .filter(Boolean)
  .join(' → ');

/**
 * The driver's steps (driver_status.state 1–5) in English — the Status
 * column's words, the progress strip's, and the dispatch board's, so the
 * same tap reads the same way wherever staff see it.
 */
export const STEP_LABELS_EN = {
  1: 'On the way',
  2: 'At pickup',
  3: 'Passenger on board',
  4: 'Dropped off',
  5: 'No-show reported',
};

/**
 * One status for the Status column, most urgent first:
 * `{ label, tone }` with tone ok | warn | bad | info | muted.
 */
export const tripStatus = (trip) => {
  const status = trip?.status;
  if (status === 'Cancelled') return { label: 'Cancelled', tone: 'muted' };
  if (status === 'Driver No-Show' || status === 'Passenger No-Show') return { label: status, tone: 'bad' };
  const state = Number(trip?.driver_status?.state);
  if (status === 'Completed' || state === 4) return { label: 'Completed', tone: 'ok' };
  if (state === 5) return { label: STEP_LABELS_EN[5], tone: 'bad' };
  if (trip?.needs_driver) return { label: 'Needs driver', tone: 'bad' };
  if (state >= 1 && state <= 3) return { label: STEP_LABELS_EN[state], tone: 'info' };
  if (!trip?.acknowledged) return { label: 'Not acknowledged', tone: 'warn' };
  return { label: 'Acknowledged', tone: 'ok' };
};

/** A trip whose latest version the operator has not yet confirmed seeing. */
export const needsAcknowledgement = (trip) => Boolean(trip) && !trip.acknowledged;

/** 'Mercedes E-Class · NLR-482', else 'Vehicle not set'. */
export const vehicleText = (trip) => vehicleLabel(trip) || (trip?.vehicle ? 'Vehicle no longer active' : 'Vehicle not set');

/** The driver's name — never an id. */
export const driverText = (trip) => {
  if (!trip?.driver) return '';
  return trip.driver.full_name || 'Driver no longer active';
};

/** 'Guide' — a slot's role, from the dashboard's one role table; nothing for none. */
export const roleLabel = (role) => OPERATOR_ROLE_LABELS[role]?.label || '';

/**
 * The portal's error reading (portalFormat's errorMessage) with English
 * copy: the status code decides the sentence, `overrides` lets a screen be
 * more specific. A status without an English sentence falls back to an
 * English one — never to the driver screens' Hungarian.
 */
const EN_ERRORS = {
  network: 'Can\'t reach the server. Check your connection and try again.',
  400: 'Some details are missing or invalid. Check them and try again.',
  401: 'Your sign-in has expired. Ask the office for a new sign-in link.',
  403: 'This page is for operator accounts only.',
  404: 'This trip is no longer assigned to you.',
  409: 'This trip changed in the meantime. Refresh and try again.',
  server: 'Something went wrong on our side. Try again in a minute.',
  fallback: 'Something went wrong. Try again.',
};

export const operatorErrorMessage = (error, overrides = {}) => errorMessage(error, {
  ...EN_ERRORS,
  ...overrides,
});

const EN_LOAD_FAILED = 'Couldn\'t load this right now. Try again later.';

/**
 * The same for loading a screen: only a real network failure blames the
 * connection; a refusal (403), a server fault and an ended session keep
 * their own sentences, and the statuses that only mean something for a
 * write read as "not now".
 */
export const operatorLoadErrorMessage = (error) => operatorErrorMessage(error, {
  400: EN_LOAD_FAILED,
  404: EN_LOAD_FAILED,
  409: EN_LOAD_FAILED,
  fallback: EN_LOAD_FAILED,
});

/* --- trip details & receipts ---------------------------------------------
 * English copy for the driver portal's shared components on the operator's
 * trip details (TripDetails.jsx). Each object is the twin of the Hungarian
 * one in portalFormat.js (same keys) and is passed as that component's
 * `copy` prop — the components themselves are the driver's, not forks.
 */

/** The trip's two ends on the route rail (RouteRail's endpointStops). */
export const ENDPOINT_LABELS_EN = { from: 'Pickup', to: 'Drop-off' };

/**
 * The word on the "now" line across a day of trips (Components/NowLine),
 * on the STAFF screens — the dispatch board and the office's own Daily
 * Overview. The driver portal's Hungarian twin is portalFormat's NOW_LABEL.
 */
export const NOW_LABEL_EN = 'Now';

/**
 * DayPickerSheet — the driver's month view, on a staff screen.
 *
 * `locale: 'en'` is moment's own built-in English, so the calendar names its
 * days and months in English whatever locale the page has loaded: the sheet's
 * module registers Hungarian for the driver, and the admin app must never
 * inherit it. Same reasoning as `inEnglish` above, one library along.
 */
export const DAY_PICKER_COPY_EN = {
  title: 'Pick a day',
  closeLabel: 'Close',
  locale: 'en',
  trips: (n) => `, ${plural(n, 'trip', 'trips')}`,
};

/**
 * PushSetting — the office's words for phone notifications, the driver's
 * component in English (portalFormat's PUSH_COPY is the Hungarian twin).
 *
 * WHAT A DISPATCHER IS PROMISED, AND NOTHING MORE. A push is the dashboard
 * bell's twin: the same rows, on this device. WHICH of them reach anybody is
 * decided by the per-type preferences they already have on the dashboard,
 * and this screen does not offer a second set — it says where they live
 * (DispatcherDevice) and leaves them there.
 *
 * `unavailable` is the same four honest reasons, said to a colleague rather
 * than to a driver: an iPhone still in Safari is told to add the board to
 * the Home Screen, which is the very thing the offer beside this one does.
 */
export const PUSH_COPY_EN = {
  head: 'Notifications on this device',
  invite: 'Get notified on this phone?',
  onHead: 'Notifications are on',
  what: 'You get the notifications your dashboard bell shows you, on this device.',
  onBody: (what) => `On for this device. ${what}`,
  enable: 'Turn on notifications',
  enabling: 'Turning on…',
  disable: 'Turn off on this device',
  disabling: 'Turning off…',
  later: 'Not now',
  laterHint: 'You can turn them on later from the bell at the top of the board.',
  unavailable: {
    [PUSH_SUPPORT.IOS_NEEDS_HOME_SCREEN]: 'On an iPhone or iPad, notifications work only from '
      + 'the Home Screen icon. Add the board to your Home Screen, open it from the icon, '
      + 'and turn them on there.',
    [PUSH_SUPPORT.DENIED]: 'You have blocked notifications for this site, so the browser will '
      + 'not ask again. Allow them in your browser settings for this site, then reload.',
    [PUSH_SUPPORT.UNSUPPORTED]: 'This browser cannot receive notifications. '
      + 'Chrome, Edge, Firefox and Safari 16.4+ can.',
    [PUSH_SUPPORT.NOT_CONFIGURED]: 'Notifications are not switched on for this deployment yet. '
      + 'Your dashboard bell and emails are unaffected.',
  },
};

/**
 * The flight's words in English, keyed as portalFormat's FLIGHT_COPY —
 * `flightBadge` reads them on the office's Daily Overview, so a cancelled
 * flight reads as cancelled there exactly as it does to the driver standing
 * in arrivals.
 */
export const FLIGHT_COPY_EN = {
  title: 'Flight',
  onTime: 'On time',
  delayed: (minutes) => `${minutes} min late`,
  scheduled: 'Scheduled',
  expected: 'Expected',
  landed: 'Landed',
  cancelled: 'Cancelled',
  arrivalAirport: 'Arriving at',
};

/** The driver's four progress steps (ProgressStrip), as the Status column names them. */
export const PROGRESS_STEPS_EN = [1, 2, 3, 4].map((state) => STEP_LABELS_EN[state]);

/**
 * Child seats named as the English booking site names them (web-mono ui-next
 * messages/en/booking.json), with the same age and weight ranges the driver
 * screens show in Hungarian — so the operator sends the seat the customer
 * actually picked.
 */
export const CHILD_SEATS_EN = [
  { field: 'booster_seat', name: 'booster seat', range: '4–10 years, 22–36 kg' },
  { field: 'convertible_seat', name: 'convertible seat', range: '1–4 years, 9–25 kg' },
  { field: 'infant_carrier', name: 'infant carrier', range: '0–1 years, up to 10 kg' },
];

/** passengerSummary's words in English: '2 adults · 1 booster seat (4–10 years, 22–36 kg)'. */
export const PASSENGER_WORDS_EN = {
  adults: (n) => plural(n, 'adult', 'adults'),
  children: (n) => plural(n, 'child', 'children'),
  seats: CHILD_SEATS_EN,
  seat: (n, { name, range }) => `${plural(n, name, `${name}s`)} (${range})`,
};

const DURATION_UNITS_EN = { about: 'about', hours: 'h', minutes: 'min' };

/** PortalRouteMap. */
export const ROUTE_MAP_COPY_EN = {
  route: 'Route',
  pickup: 'Pickup',
  dropoff: 'Drop-off',
  stops: (n) => `via ${plural(n, 'stop', 'stops')}`,
  navigate: 'Open in maps',
  distance: (km) => formatDistance(km, '.'),
  duration: (minutes) => formatDuration(minutes, DURATION_UNITS_EN),
  // ROUTE_MAP_COPY's 'órás program': the booked length, and no "about" — the
  // drive time is a routing guess, the programme's hours are what was sold.
  programmeDuration: (hours) => `${formatHours(hours, '.')}-hour programme`,
};

/** TripCards: the programme, the manifest and the customer. */
export const TRIP_CARD_COPY_EN = {
  programme: 'Programme',
  programmeUnnamed: 'Programme with stops',
  // The other real shape: hours booked, no itinerary.
  programmeNoStops: 'Programme without stops',
  endpoints: ENDPOINT_LABELS_EN,
  hours: (n) => plural(n, 'hour', 'hours'),
  stop: 'Stop',
  stay: (n) => `${n} min`,
  passengers: 'Passengers',
  people: (n) => plural(n, 'person', 'people'),
  flight: (flight) => `Flight ${flight}`,
  phoneHint: (trip) => {
    const at = phoneReleaseAt(trip);
    const when = at ? ` (${shortDay(at)} ${formatTime(at)})` : '';
    return `The customer's phone appears 24 hours before pickup${when}.`;
  },
  call: 'Call',
};

// DriverExpenseService's three states, with the driver screens' tones.
const EXPENSE_STATUS_EN = {
  submitted: { label: 'Submitted', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'ok' },
  rejected: { label: 'Rejected', tone: 'bad' },
};

/** A receipt's or an overtime entry's review state (overtime 'pending' reads 'Submitted'). */
const claimStatusEn = (status) => EXPENSE_STATUS_EN[status] || EXPENSE_STATUS_EN.submitted;

/** OvertimeCard — minutes only, never money. */
export const OVERTIME_COPY_EN = {
  title: 'Overtime',
  stages: { pickup: 'At pickup', dropoff: 'At drop-off' },
  methods: { timer: 'Timed', manual: 'Reported afterwards' },
  running: 'Running',
  minutes: (n) => `${n} min`,
  total: (n) => `Total: ${n} min`,
  status: claimStatusEn,
};

const EXPENSE_TYPES_EN = [
  { value: 'parking', label: 'Parking' },
  { value: 'other', label: 'Other' },
];

// The API's `unreadable_reasons` vocabulary in English. Same codes as the
// driver screens' SCAN_REASONS_HU — if the API adds one, both files gain a
// line, and until then both fall back to the generic sentence rather than
// printing the raw code.
const SCAN_REASONS_EN = {
  blurry: 'The photo is too blurry.',
  total_illegible: 'The total can\'t be made out.',
  cut_off: 'Part of the receipt is cut off.',
  too_dark: 'The photo is too dark.',
  not_a_receipt: 'There is no receipt in this photo.',
  scan_unavailable: 'This photo couldn\'t be read just now.',
};

const SCAN_REASON_FALLBACK_EN = 'This photo couldn\'t be read.';

/**
 * RecordExpense, ExpenseSheet and ExpenseList on ONE trip: the claim is
 * always tied to that trip, so the list never repeats which trip it is for.
 * The amount is the operator's own typed-in claim, never a booking price.
 */
export const EXPENSE_COPY_EN = {
  record: 'Record expense',
  recordForTrip: 'Record expense for this trip',
  saved: 'Expense recorded and sent for approval.',
  title: 'Record expense',
  forTrip: (label) => `For trip ${label}`,
  closeLabel: 'Close',
  typeLabel: 'Type',
  types: EXPENSE_TYPES_EN,
  amountLabel: 'Amount',
  currencyLabel: 'Currency',
  photoAlt: 'Photo of the receipt',
  photoReady: 'Photo ready',
  retake: 'Replace photo',
  processing: 'Processing…',
  pickPhoto: 'Photo of the receipt',
  processingPhoto: 'Processing the photo…',
  noteLabel: 'Note',
  optional: ' · optional',
  notePlaceholder: 'E.g. Terminal 2 car park, 40 minutes',
  submit: 'Submit',
  sending: 'Sending…',
  // The BROWSER could not even decode the file (imageDownscale threw) — a
  // different thing from a photo the scan could not read, which gets the
  // `unreadable*` wording below and an escape hatch.
  photoUnreadable: 'This photo couldn\'t be read. Try another one.',
  amountMissing: 'Enter the amount (e.g. 2500 or 12.50).',
  photoMissing: 'Add a photo of the receipt or parking ticket.',

  // The twin of the driver sheet's scan copy — same keys, same decisions,
  // English words. See EXPENSE_COPY in ../portalFormat for why each one is
  // worded the way it is; the reasoning is not repeated here.
  scanning: 'Reading the receipt…',
  scanTitle: 'Read from the photo',
  scanAmountLabel: 'Amount',
  scanDateLabel: 'Dated',
  scanPlaceLabel: 'Place',
  scanMissing: 'not readable',
  scanHint: 'From the photo. Change it if it\'s wrong.',
  amountPlaceholder: 'Type the total',
  // See the Hungarian twin: the submit is disabled while the scan is in
  // flight, so both of these exist to say why rather than leave a dead button.
  amountScanning: 'Reading the total…',
  scanWait: 'Reading…',

  unreadableTitle: 'Can\'t read this photo',
  unreadableReasons: (codes) => scanReasonLines(codes, SCAN_REASONS_EN, SCAN_REASON_FALLBACK_EN),
  retakePhoto: 'Retake photo',
  sendAnyway: 'Send it anyway',
  sendAnywayNote: 'The office will see that this one wasn\'t scanned.',

  duplicateTitle: 'You may have sent this one already',
  duplicateWhen: (when) => `Sent ${when}`,
  duplicateSame: 'The same photo.',
  duplicateDifferent: 'This is a different receipt',
  duplicateCancel: 'Cancel — it\'s the same one',
  submitError: (failure) => operatorErrorMessage(failure, {
    404: 'This trip is no longer assigned to you, so no expense can be filed against it.',
    413: 'The photo is too large. Try a smaller one.',
  }),
  listTitle: 'Expenses for this trip',
  count: (n) => String(n),
  more: (n) => `+${plural(n, 'earlier expense', 'earlier expenses')}`,
  when: (value) => (value ? `${shortDay(value)} ${formatTime(value)}` : ''),
  typeName: (type) => (
    EXPENSE_TYPES_EN.find((option) => option.value === type) || EXPENSE_TYPES_EN[1]
  ).label,
  status: claimStatusEn,
  amount: formatMoney,
  tripText: () => '',
};
